import { DirectSpeech, type DirectState } from "./directSpeech";
import { z } from "zod";
import type { RadioMessage } from "../domain/model";
import {
  RadioQueue,
  RadioTraffic,
  radioPriority,
  spokenText,
  type SpeechMessage,
} from "./radioQueue";
import {
  germanVoices,
  radioSpeakerKey,
  selectRadioVoice,
  type VoiceGender,
  neuralGender,
} from "./radioVoices";
import { speechTransmission, voicePreview } from "./speechText";

export const AudioPreferencesSchema = z.object({
  enabled: z.boolean().default(true),
  effects: z.boolean().default(true),
  speech: z.boolean().default(true),
  speakStatuses: z.boolean().default(false),
  radioNoise: z.boolean().default(true),
  speechPolicyVersion: z.literal(2).default(2),
  speechProvider: z.enum(["auto", "browser"]).default("auto"),
  playback: z.enum(["direct", "filtered"]).default("direct"),
  neuralGender: z.enum(["male", "female"]).default("male"),
  volume: z.number().min(0).max(1).default(0.6),
  rate: z.number().min(0.8).max(1.3).default(0.95),
  voiceMode: z.enum(["mixed", "single"]).default("mixed"),
  femaleVoiceURI: z.string().max(500).default(""),
  maleVoiceURI: z.string().max(500).default(""),
  voiceSeed: z
    .number()
    .int()
    .min(0)
    .max(4294967295)
    .default(() => Math.floor(Math.random() * 4294967296)),
  voiceURI: z.string().max(500).default(""),
});
export type AudioPreferences = z.infer<typeof AudioPreferencesSchema>;
export function readAudioPreferences(value: unknown): AudioPreferences {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const saved = { ...value } as Record<string, unknown>;
    if (saved.speechPolicyVersion !== 2) {
      saved.speakStatuses = false;
      saved.speechPolicyVersion = 2;
    }
    // Migrate the old default pace, while retaining deliberately chosen speeds and mute.
    if (!("voiceMode" in saved) && saved.rate === 1)
      return AudioPreferencesSchema.parse({ ...saved, rate: 0.95 });
    return AudioPreferencesSchema.parse(saved);
  }
  return AudioPreferencesSchema.parse(value);
}
export const defaultAudioPreferences = AudioPreferencesSchema.parse({});
export type AudioRuntime = DirectState & {
  playback: "direct" | "filtered";
  callerSpeaking: boolean;
  unlocked: boolean;
  speaking: boolean;
  currentId?: string;
  currentText: string;
  currentVoice: string;
  neuralAvailable: boolean;
  queued: number;
  outputLevel: number;
  error: string;
};
export const silentRuntime: AudioRuntime = {
  directPhase: "idle",
  directReady: false,
  directSeconds: 0,
  directError: "",
  playback: "filtered",
  callerSpeaking: false,
  unlocked: false,
  speaking: false,
  currentText: "",
  currentVoice: "",
  neuralAvailable: false,
  queued: 0,
  outputLevel: 0,
  error: "",
};

export class RadioAudio {
  private direct = new DirectSpeech((state) =>
    this.update({
      ...state,
      error:
        state.directError ||
        (state.directPhase === "playing" ? "" : this.runtime.error),
    }),
  );
  private get useDirect() {
    return this.preferences.playback === "direct" && this.direct.attached;
  }
  attachDirect(element: HTMLAudioElement | null) {
    this.direct.attach(element);
    this.update({ playback: this.useDirect ? "direct" : "filtered" });
  }
  private context?: AudioContext;
  private master?: GainNode;
  private analyser?: AnalyserNode;
  private meter?: ReturnType<typeof setInterval>;
  private queue = new RadioQueue();
  private traffic = new RadioTraffic();
  private activeMessage?: SpeechMessage;
  private activeTransmission = 0;
  private neuralAbort?: AbortController;
  private neuralRetryAt = 0;
  private nodes = new Set<AudioScheduledSourceNode>();
  private generation = 0;
  private playing = false;
  private alive = true;
  private signalUntil = 0;
  private finishSpeech?: () => void;
  private finishPause?: () => void;
  private runtime: AudioRuntime = { ...silentRuntime };
  constructor(
    private preferences: AudioPreferences,
    private notify: (runtime: AudioRuntime) => void,
  ) {}
  private update(values: Partial<AudioRuntime>) {
    this.runtime = { ...this.runtime, ...values, queued: this.queue.length };
    if (this.alive) this.notify(this.runtime);
  }
  setPreferences(preferences: AudioPreferences) {
    const stopVoice = this.preferences.speech && !preferences.speech;
    const switchOutput = this.preferences.playback !== preferences.playback;
    this.preferences = preferences;
    this.direct.volume(preferences.volume, preferences.enabled);
    this.update({ playback: this.useDirect ? "direct" : "filtered" });
    if (switchOutput) this.stop();
    if (this.master && this.context)
      this.master.gain.setValueAtTime(
        preferences.enabled ? preferences.volume : 0,
        this.context.currentTime,
      );
    if (!preferences.enabled || !preferences.volume || stopVoice) this.stop();
  }
  /** Hold only while telephone audio is on air, never for the whole open call. */
  setCallerSpeaking(speaking: boolean) {
    if (!this.alive || speaking === this.runtime.callerSpeaking) return;
    const interrupted =
      this.activeMessage &&
      this.activeTransmission < this.activeMessage.transmissions.length
        ? { ...this.activeMessage, resumeFrom: this.activeTransmission }
        : undefined;
    this.update({ callerSpeaking: speaking });
    if (speaking) {
      this.stop(false);
      if (interrupted) this.queue.add(interrupted);
      this.update({});
    } else {
      void this.drain();
    }
  }
  setNeuralAvailable(available: boolean) {
    this.update({ neuralAvailable: available });
  }
  async prepare() {
    if (!this.preferences.enabled || !this.preferences.volume) return;
    // Called synchronously from the dispatcher's gesture, before the command request.
    if (
      !this.runtime.unlocked ||
      (!this.useDirect && this.context?.state !== "running")
    )
      await this.activate(false);
  }
  private startMeter() {
    this.stopMeter();
    if (!this.analyser) return;
    const samples = new Uint8Array(this.analyser.fftSize);
    this.meter = setInterval(() => {
      if (!this.alive) return;
      this.analyser!.getByteTimeDomainData(samples);
      let peak = 0;
      for (const value of samples)
        peak = Math.max(peak, Math.abs(value - 128) / 128);
      const level =
        this.context?.state === "running" ? Math.round(peak * 100) / 100 : 0;
      if (level !== this.runtime.outputLevel)
        this.update({ outputLevel: level });
    }, 120);
  }
  private stopMeter() {
    clearInterval(this.meter);
    this.meter = undefined;
    this.update({ outputLevel: 0 });
  }
  async activate(signal = true) {
    // Play is invoked while the click still carries user activation, before any fetch/await.
    const directReady = this.useDirect
      ? this.direct.unlock(this.preferences.volume)
      : Promise.resolve(false);
    try {
      if (!this.context || this.context.state === "closed") {
        this.context = new AudioContext();
        this.master = this.context.createGain();
        if (typeof this.context.createAnalyser === "function") {
          this.analyser = this.context.createAnalyser();
          this.analyser.fftSize = 512;
          this.master.connect(this.analyser);
          this.analyser.connect(this.context.destination);
        } else this.master.connect(this.context.destination);
        this.context.onstatechange = () => {
          if (!this.alive || this.useDirect) return;
          const running = this.context?.state === "running";
          this.update({
            unlocked: running,
            error: running
              ? ""
              : "Browser hat die Tonausgabe pausiert. Bitte Funk aktivieren.",
          });
        };
      }
      this.master!.gain.setValueAtTime(
        this.preferences.enabled ? this.preferences.volume : 0,
        this.context.currentTime,
      );
      let deadline: ReturnType<typeof setTimeout> | undefined;
      try {
        await Promise.race([
          this.context.resume(),
          new Promise<never>((_, reject) => {
            deadline = setTimeout(
              () => reject(Error("Audiofreigabe ausstehend")),
              1500,
            );
          }),
        ]);
      } finally {
        clearTimeout(deadline);
      }
      if (!this.alive) return;
      await Promise.race([
        directReady,
        new Promise((resolve) => setTimeout(resolve, 300)),
      ]);
      this.update({
        unlocked: this.useDirect || this.context.state === "running",
        error: "",
      });
      if (!this.useDirect && this.context.state !== "running")
        throw Error(
          "Ton ist noch gesperrt. Bitte Funk aktivieren erneut anklicken.",
        );
      if (signal) this.signal("status");
      void this.drain();
    } catch {
      if (this.useDirect) {
        this.update({ unlocked: true, error: "" });
        void this.drain();
      } else
        this.update({
          unlocked: false,
          error:
            "Ton konnte nicht gestartet werden. Bitte Direktton verwenden oder Funk erneut aktivieren.",
        });
    }
  }
  receive(messages: RadioMessage[]) {
    if (
      !this.runtime.unlocked ||
      !this.preferences.enabled ||
      !this.preferences.volume ||
      !messages.length
    )
      return;
    const strongest = [...messages].sort(
      (a, b) => radioPriority[b.kind] - radioPriority[a.kind],
    )[0];
    this.signal(strongest.kind);
    if (this.preferences.speech)
      for (const m of messages) {
        if (m.kind === "status" && !this.preferences.speakStatuses) continue;
        if (!this.traffic.accept(m)) continue;
        this.queue.add(m);
        // Finish the transmission on air. Priority only changes the waiting queue;
        // repeated warnings must never cut a speaker off halfway through a word.
      }
    this.update({});
    void this.drain();
  }
  replay(message: RadioMessage) {
    this.stop(false);
    this.neuralRetryAt = 0;
    this.queue.add({
      ...message,
      id: `replay-${message.id}-${Date.now()}`,
      sourceId: message.id,
      manual: true,
    });
    this.update({});
    void this.drain();
  }
  preview() {
    this.stop();
    this.neuralRetryAt = 0;
    this.queue.add({
      id: `voice-preview-${Date.now()}`,
      sequence: 0,
      at: Date.now(),
      kind: "request",
      voicePreview: true,
      transmissions: voicePreview,
    });
    this.update({});
    void this.drain();
  }
  private pause(milliseconds: number) {
    return new Promise<void>((resolve) => {
      const finish = () => {
        clearTimeout(timer);
        this.finishPause = undefined;
        resolve();
      };
      const timer = setTimeout(finish, milliseconds);
      this.finishPause = finish;
    });
  }
  private tone(
    frequency: number,
    at: number,
    duration: number,
    level = 0.13,
    type: OscillatorType = "sine",
  ) {
    if (!this.context || !this.master) return;
    const tone = this.context.createOscillator(),
      gain = this.context.createGain();
    tone.type = type;
    tone.frequency.value = frequency;
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(level, at + 0.008);
    gain.gain.setValueAtTime(level, at + duration - 0.015);
    gain.gain.linearRampToValueAtTime(0, at + duration);
    tone.connect(gain).connect(this.master);
    this.nodes.add(tone);
    tone.onended = () => {
      this.nodes.delete(tone);
      tone.disconnect();
      gain.disconnect();
    };
    tone.start(at);
    tone.stop(at + duration + 0.01);
  }
  private signal(kind: RadioMessage["kind"]) {
    if (
      this.runtime.callerSpeaking ||
      !this.preferences.effects ||
      !this.preferences.enabled ||
      !this.context ||
      this.context.state !== "running"
    )
      return;
    // Routine digital acknowledgements must not mask syllables on an occupied channel.
    const onAir = this.runtime.speaking;
    if (onAir && !["call", "alarm", "warning"].includes(kind)) return;
    const playTone = (
      frequency: number,
      at: number,
      duration: number,
      level = 0.13,
      type: OscillatorType = "sine",
    ) => this.tone(frequency, at, duration, level * (onAir ? 0.25 : 1), type);
    // Short synthesized receiver sounds; no copyrighted recordings or live alarm address.
    const at = Math.max(this.context.currentTime + 0.01, this.signalUntil);
    if (at - this.context.currentTime > 2) return;
    if (kind === "call") {
      for (const offset of [0, 0.48, 0.96]) {
        playTone(880, at + offset, 0.3, 0.1, "triangle");
        playTone(1175, at + offset, 0.3, 0.07);
      }
      this.signalUntil = at + 1.3;
    } else if (kind === "alarm") {
      [1060, 1160, 1270, 1400, 1530].forEach((f, i) =>
        playTone(f, at + i * 0.085, 0.075),
      );
      for (const offset of [0.62, 0.86, 1.1])
        playTone(1900, at + offset, 0.15, 0.09, "triangle");
      this.signalUntil = at + 1.3;
    } else if (kind === "speechRequest") {
      playTone(1500, at, 0.09, 0.08);
      playTone(1500, at + 0.18, 0.09, 0.08);
    } else if (kind === "warning") {
      for (const offset of [0, 0.3]) {
        playTone(780, at + offset, 0.12);
        playTone(1040, at + offset + 0.14, 0.12);
      }
      this.signalUntil = at + 0.6;
    } else {
      playTone(kind === "clear" ? 800 : 1200, at, 0.075, 0.08);
      playTone(kind === "clear" ? 600 : 900, at + 0.11, 0.065, 0.06);
      this.signalUntil = at + 0.2;
    }
  }
  private squelch(tail = false, at = this.context?.currentTime ?? 0) {
    if (
      this.runtime.callerSpeaking ||
      !this.preferences.radioNoise ||
      !this.preferences.enabled ||
      !this.context ||
      !this.master ||
      this.context.state !== "running"
    )
      return;
    if (tail) {
      this.tone(1550, at, 0.04, 0.035);
      this.tone(1150, at + 0.055, 0.04, 0.025);
      return;
    }
    const duration = 0.12;
    const buffer = this.context.createBuffer(
      1,
      Math.ceil(this.context.sampleRate * duration),
      this.context.sampleRate,
    );
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const source = this.context.createBufferSource(),
      high = this.context.createBiquadFilter(),
      low = this.context.createBiquadFilter(),
      gain = this.context.createGain();
    source.buffer = buffer;
    high.type = "highpass";
    high.frequency.value = 1100;
    high.Q.value = 0.7;
    low.type = "lowpass";
    low.frequency.value = 3200;
    low.Q.value = 0.7;
    // Receiver opening only. Never add a noise burst to the end of speech.
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(0.12, at + 0.008);
    gain.gain.setValueAtTime(0.12, at + duration - 0.018);
    gain.gain.linearRampToValueAtTime(0, at + duration);
    source.connect(high).connect(low).connect(gain).connect(this.master);
    this.nodes.add(source);
    source.onended = () => {
      this.nodes.delete(source);
      source.disconnect();
      high.disconnect();
      low.disconnect();
      gain.disconnect();
    };
    source.start(at);
    source.stop(at + duration);
    this.tone(1750, at + 0.015, 0.04, 0.05);
    this.tone(2150, at + 0.075, 0.04, 0.04);
  }

  private async drain() {
    if (
      this.playing ||
      this.runtime.callerSpeaking ||
      !this.alive ||
      !this.runtime.unlocked ||
      !this.preferences.enabled ||
      !this.preferences.speech
    )
      return;
    if (!("speechSynthesis" in window) && !this.runtime.neuralAvailable) {
      this.queue.clear();
      this.update({
        error:
          "Dieser Browser bietet keine Sprachausgabe. Signale und Funktexte bleiben verfügbar.",
      });
      return;
    }
    this.playing = true;
    const generation = this.generation;
    try {
      while (generation === this.generation && this.alive) {
        const message = this.queue.next();
        if (!message) break;
        this.activeMessage = message;
        for (const index of message.transmissions.keys()) {
          if (index < (message.resumeFrom ?? 0)) continue;
          this.activeTransmission = index;
          const transmission = message.voicePreview
            ? message.transmissions[index]
            : speechTransmission(message, index, message.manual)!;
          if (generation !== this.generation || !this.alive) break;
          this.update({
            speaking: true,
            currentId: message.id,
            currentText: transmission.text,
            currentVoice: "",
          });
          const speaker = radioSpeakerKey(message, transmission.sender);
          const previewGender: VoiceGender | undefined = message.voicePreview
            ? index === 1
              ? "male"
              : "female"
            : undefined;
          let rendered = false;
          if (
            this.preferences.speechProvider === "auto" &&
            this.runtime.neuralAvailable &&
            Date.now() >= this.neuralRetryAt
          ) {
            try {
              rendered = await this.speakNeural(
                message,
                index,
                generation,
                speaker,
                previewGender,
              );
            } catch {
              if (generation === this.generation) {
                this.neuralRetryAt = Date.now() + 30000;
                this.update({
                  error:
                    "Neuronale Stimme kurzzeitig nicht verfügbar. Browserstimme übernimmt.",
                });
              }
            }
          }
          if (!rendered && generation === this.generation && this.alive)
            await this.speak(
              transmission.text,
              generation,
              speaker,
              previewGender,
            );
          if (generation === this.generation && this.alive) {
            this.activeTransmission = index + 1;
            await this.pause(280);
          }
        }
      }
    } finally {
      if (generation === this.generation) {
        this.playing = false;
        this.activeMessage = undefined;
        this.stopMeter();
        this.update({
          speaking: false,
          currentId: undefined,
          currentText: "",
          currentVoice: "",
        });
      }
    }
  }
  private async speakNeural(
    message: SpeechMessage,
    index: number,
    generation: number,
    speaker: string,
    previewGender?: VoiceGender,
  ) {
    const abort = new AbortController();
    this.neuralAbort = abort;
    const deadline = setTimeout(() => abort.abort(), 13000);
    const gender =
      this.preferences.voiceMode === "single"
        ? this.preferences.neuralGender
        : (previewGender ?? neuralGender(this.preferences.voiceSeed, speaker));
    try {
      const response = await fetch("/api/speech", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: abort.signal,
        body: JSON.stringify({
          messageId: message.sourceId ?? message.id,
          index,
          preview: !!message.voicePreview,
          full: !!message.manual,
          gender,
          rate: this.preferences.rate,
        }),
      });
      if (!response.ok) throw Error("speech unavailable");
      const bytes = await response.arrayBuffer();
      clearTimeout(deadline);
      if (generation !== this.generation || !this.alive) return true;
      this.update({
        currentVoice:
          gender === "male"
            ? "Thorsten · neuronales Modell"
            : "Kerstin · neuronales Modell",
        error: "",
      });
      if (this.useDirect) {
        const openingAt = Math.max(
          this.context?.currentTime ?? 0,
          this.signalUntil,
        );
        this.squelch(false, openingAt);
        const lead =
          Math.max(0, openingAt - (this.context?.currentTime ?? 0)) * 1000 +
          (this.preferences.radioNoise ? 180 : 0);
        if (lead) await this.pause(lead);
        if (generation !== this.generation || !this.alive) return true;
        await this.direct.play(
          new Blob([bytes], { type: "audio/wav" }),
          this.preferences.volume,
        );
        if (generation === this.generation) this.squelch(true);
        return true;
      }
      const buffer = await this.context!.decodeAudioData(bytes);
      if (generation !== this.generation || !this.alive) return true;
      await new Promise<void>((resolve) => {
        const source = this.context!.createBufferSource();
        const gain = this.context!.createGain();
        const high = this.context!.createBiquadFilter();
        const low = this.context!.createBiquadFilter();
        source.buffer = buffer;
        high.type = "highpass";
        high.frequency.value = this.preferences.radioNoise ? 260 : 20;
        low.type = "lowpass";
        low.frequency.value = this.preferences.radioNoise ? 3700 : 20000;
        const openingAt = Math.max(this.context!.currentTime, this.signalUntil);
        const at = openingAt + (this.preferences.radioNoise ? 0.18 : 0);
        const fade = Math.min(0.008, buffer.duration / 3);
        gain.gain.setValueAtTime(0, at);
        gain.gain.linearRampToValueAtTime(1, at + fade);
        gain.gain.setValueAtTime(1, at + buffer.duration - fade);
        gain.gain.linearRampToValueAtTime(0, at + buffer.duration);
        source.connect(high).connect(low).connect(gain).connect(this.master!);
        this.nodes.add(source);
        const finish = () => {
          this.nodes.delete(source);
          source.disconnect();
          high.disconnect();
          low.disconnect();
          gain.disconnect();
          this.finishSpeech = undefined;
          this.stopMeter();
          resolve();
        };
        source.onended = () => {
          if (generation === this.generation) this.squelch(true);
          finish();
        };
        this.finishSpeech = () => {
          source.onended = null;
          try {
            source.stop();
          } catch {}
          finish();
        };
        this.squelch(false, openingAt);
        this.startMeter();
        source.start(at);
      });
      return true;
    } finally {
      clearTimeout(deadline);
      if (this.neuralAbort === abort) this.neuralAbort = undefined;
    }
  }
  private async speak(
    text: string,
    generation: number,
    speaker: string,
    previewGender?: VoiceGender,
  ) {
    const now = this.context?.currentTime ?? 0;
    const openingAt = Math.max(now, this.signalUntil);
    this.squelch(false, openingAt);
    const lead =
      (openingAt - now) * 1000 + (this.preferences.radioNoise ? 180 : 0);
    if (lead > 0) {
      await this.pause(lead);
      if (generation !== this.generation || !this.alive) return;
    }
    return new Promise<void>((resolve) => {
      const voices = germanVoices(window.speechSynthesis?.getVoices() ?? []);
      if (!voices.length) {
        this.update({
          error:
            "Keine deutsche Stimme verfügbar. Bitte eine deutsche Systemstimme installieren oder einen anderen Browser verwenden.",
        });
        resolve();
        return;
      }
      const utterance = new SpeechSynthesisUtterance(spokenText(text));
      const selected = selectRadioVoice(
        voices,
        this.preferences,
        speaker,
        previewGender,
      );
      utterance.lang = selected.voice?.lang ?? "de-DE";
      utterance.rate = selected.rate;
      utterance.pitch = selected.pitch;
      utterance.volume = this.preferences.volume;
      utterance.voice = selected.voice ?? null;
      this.update({
        currentVoice: selected.voice?.name ?? "",
        error:
          this.neuralRetryAt > Date.now()
            ? "Sprachdienst beschäftigt: Browserstimme übernimmt vorübergehend."
            : "",
      });
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(timeout);
        this.finishSpeech = undefined;
        resolve();
      };
      const timeout = setTimeout(() => {
        if (generation === this.generation) {
          this.update({
            error:
              "Sprachausgabe reagiert nicht. Stimme wechseln oder Ton erneut aktivieren.",
          });
          window.speechSynthesis.cancel();
        }
        finish();
      }, 60000);
      this.finishSpeech = finish;
      utterance.onend = () => {
        if (generation === this.generation) this.squelch(true);
        finish();
      };
      utterance.onerror = (event) => {
        if (
          generation === this.generation &&
          !["interrupted", "canceled"].includes(event.error)
        )
          this.update({
            error:
              "Sprachausgabe nicht verfügbar. Stimme und Browser-Tonausgabe prüfen.",
          });
        finish();
      };
      try {
        window.speechSynthesis.speak(utterance);
      } catch {
        this.update({ error: "Sprachausgabe konnte nicht gestartet werden." });
        finish();
      }
    });
  }
  stop(clearQueue = true) {
    this.stopMeter();
    this.generation++;
    this.direct.stop();
    if (clearQueue) this.queue.clear();
    this.neuralAbort?.abort();
    this.activeMessage = undefined;
    this.playing = false;
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    this.finishSpeech?.();
    this.finishPause?.();
    for (const node of this.nodes) {
      try {
        node.stop();
      } catch {}
    }
    this.nodes.clear();
    this.signalUntil = 0;
    this.update({
      speaking: false,
      currentId: undefined,
      currentText: "",
      currentVoice: "",
    });
  }
  dispose() {
    this.alive = false;
    this.stop();
    this.direct.dispose();
    void this.context?.close();
  }
}
